import { Box, Typography } from '@mui/material';
import { memo, useEffect, useRef, useState } from 'react';

import Divider from '../../../../../common/divider/divider';
import useWindowSize from '../../../../../common/hooks/useWindowSize';
import { formatToAmount } from '../../../../../../constants/helpers/utils';
import { convertToKebabCase } from '../../../../../../constants/helpers/text';

import { NO_DATA } from './const';
import { ChartData } from './interface';
import LegendIndicator from './chartLegendIndicator';

interface Props {
  chartData: ChartData[];
  chartTotal: number;
}

const removeEmptyDataFlag = (chartData: ChartData[]): ChartData[] => chartData.filter(({ name }) => name !== NO_DATA);

const ChartLegend = ({ chartData, chartTotal }: Props) => {
  const [hasScroll, setHasScroll] = useState<boolean>(false);
  const { width, height } = useWindowSize();
  const legendRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (legendRef?.current && legendRef.current.scrollHeight > legendRef.current.offsetHeight) {
      setHasScroll(true);
    } else {
      setHasScroll(false);
    }
  }, [width, height]);

  return (
    <Box
      data-testid="chart-legend"
      px={3}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      width="100%"
      height="25%"
    >
      <Box ref={legendRef} overflow="auto" pr={hasScroll ? 2 : 0}>
        {removeEmptyDataFlag(chartData).map(({ name, value, color }: ChartData, index: number) => (
          <Box display="flex" justifyContent="space-between" mb={1} key={index}>
            <Box display="flex" alignItems="center">
              <LegendIndicator color={color} />
              <Typography variant="h4">{name}</Typography>
            </Box>
            <Typography data-testid={`${convertToKebabCase(name)}-value`} variant="h4">
              {formatToAmount(value)}
            </Typography>
          </Box>
        ))}
      </Box>
      <Divider sx={{ flex: '0 !important' }} />
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h3">Total</Typography>
        <Typography data-testid="total-value" variant="h3">
          {formatToAmount(chartTotal)}
        </Typography>
      </Box>
    </Box>
  );
};

export default memo(ChartLegend);
